<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.container"
  >
    <div :style="style.rowSkillName">
      <div :style="style.label">
        <TextFormLabelSecondary>
          Skill:
        </TextFormLabelSecondary>
      </div>
      <div :style="style.formSkillNameContainer">
        <InputSkillPrimary
          :disabled="disabled || freeze || disableInputSkillName"
          :disableValidator="true"
          placeholder="Enter skill name"
          v-model="form.data.skillName"
          @change-input="updateFormData"
          :style="style.inputSkill"
        />
      </div>
    </div>
    <div :style="style.rowSkillStatus">
      <div :style="style.label">
        <TextFormLabelSecondary>
          Status:
        </TextFormLabelSecondary>
      </div>
      <div :style="style.formSkillStatusContainer">
        <MultiTogglePrimary
          v-model="form.data.skillStatusTypeId"
          :bindByIdKey="true"
          :disabled="disabled"
          :freeze="freeze"
          :idKey="'id'"
          :labelKey="'name'"
          :options="skillStatusTypes"
          @change="updateFormData"
        />
      </div>
    </div>
    <div :style="style.rowExperiencePeriod">
      <div :style="style.label">
        <TextFormLabelSecondary>
          Experience:
        </TextFormLabelSecondary>
      </div>
      <div :style="style.formExperiencePeriodContainer">
        <DropdownSecondary
          v-model="form.data.periodCategoryId"
          :disabled="disabled || freeze"
          :idKey="'id'"
          :labelKey="'name'"
          :options="periodCategories"
          @change="updateFormData"
          :style="style.dropdown"
        />
      </div>
    </div>
    <div :style="style.rowLinks">
      <div :style="style.label">
        <TextFormLabelSecondary>
          Links:
        </TextFormLabelSecondary>
      </div>
      <div :style="style.formLinks">
        <TagToggleListPrimary
          v-if="linkTargetTenantUserSkills.length > 0"
          v-model="form.data.targetIds"
          :disabled="disabled"
          :freeze="freeze"
          :items="linkItems"
          @change="updateFormData"
        />
        <TextMutedPrimary v-else>
          You have no skill to link yet.
        </TextMutedPrimary>
      </div>
    </div>
  </div>
</template>

<script>
  import DropdownSecondary from '@modules/v2/dropdown/dropdown-secondary'
  import InputSkillPrimary from '@modules/v2/input/input-skill-primary'
  import MultiTogglePrimary from '@modules/v2/toggle/multi-toggle-primary'
  import TagToggleListPrimary from '@modules/v2/tag/tag-toggle-list-primary'
  import TextFormLabelSecondary from '@modules/v2/text/text-form-label-secondary'
  import TextMutedPrimary from '@modules/v2/text/text-muted-primary'
  import { Layout } from '@js/styles'
  import { mapState } from 'vuex'
  import { Z_INDEXES } from '@js/constants'

  /**
   * Usage from parent component
   * -----
   * <FormTenantUserSkillPrimary
   *   :form.sync="form"
   *   :tenantUserSkills="tenantUserSkills"
   * />
  **/
  export default {
    name: 'FormTenantUserSkillPrimary',
    inheritAttrs: false,
    components: {
      DropdownSecondary,
      InputSkillPrimary,
      MultiTogglePrimary,
      TagToggleListPrimary,
      TextFormLabelSecondary,
      TextMutedPrimary,
    },
    props: {
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      freeze: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableInputSkillName: {
        type: Boolean,
        required: false,
        default: false,
      },
      form: {
        type: Object,
        required: false,
        default: null
      },
      tenantUserSkills: {
        type: Array,
        required: true
      },
    },
    data: function () {
      return {
        selectedSkillStatusId: null,
        style: {
          container: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          },
          dropdown: {
            width: '100%',
          },
          formExperiencePeriodContainer: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
          },
          formSkillNameContainer: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
          },
          formSkillStatusContainer: {
            alignItems: 'center',
            display: 'flex',
            flex: 1,
          },
          formLinks: {
            flex: 1,
            maxHeight: '106px',
            minHeight: Layout.space.md,
            overflowX: 'hidden',
            overflowY: 'scroll',
          },
          inputSkill: {
            width: '100%',
            zIndex: Z_INDEXES.MODAL.CONFIGURE_TENANT_USER_SKILL.DROPDOWN,
          },
          label: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            minWidth: '90px',
            padding: `0 ${Layout.space.md} 0 0`,
            width: '90px',
          },
          rowExperiencePeriod: {
            alignItems: 'center',
            display: 'flex',
            margin: `${Layout.space.lg} 0 0 0`,
            width: '100%',
          },
          rowLinks: {
            alignItems: 'center',
            display: 'flex',
            margin: `${Layout.space.lg} 0 0 0`,
            width: '100%',
          },
          rowSkillName: {
            alignItems: 'center',
            display: 'flex',
            width: '100%',
          },
          rowSkillStatus: {
            alignItems: 'center',
            display: 'flex',
            margin: `${Layout.space.md} 0 0 0`,
            width: '100%',
          },
        },
      };
    },
    computed: {
      ...mapState({
        periodCategories: state =>
          state.globalVariables.data.experiencePeriodCategories,
        skillStatusTypes: state =>
          state.globalVariables.data.skillStatusTypes,
      }),
      linkItems: function () {
        return this.linkTargetTenantUserSkills
          .filter(elem =>
            this.form.params.id === null ? true : elem.id !== this.form.params.id)
          .reduce((result, elem) => {
            result.push({
              'id': elem.id,
              'label': elem.skill.name,
            });
            return result;
          }, []);
      },
      linkTargetTenantUserSkills: function () {
        const targetTenantUserSkillId = this.form.params.id;

        if (targetTenantUserSkillId === null) {
          return this.tenantUserSkills;
        }

        return this.tenantUserSkills
          .filter(elem => elem.id !== targetTenantUserSkillId);
      },
    },
    methods: {
      updateFormData: function () {
        this.$emit('update:form', this.form);
      },
    },
    watch: {
      form: {
        handler: function (newVal, oldVal) {
          this.form = newVal;
        },
        deep: true,
      },
    },
  };
</script>
